import React, { Suspense, lazy } from 'react'
import { Routes, Route } from 'react-router-dom'
import {
  TEAMS_URL,
  PLAYER_URL,
  SCHEDULE_URL,
  SCORES_URL,
  TICKETS_URL,
  MATCHUP_URL,
  EVENT_HUB_URL,
  STANDINGS_URL,
  PREDICTIONS_URL,
  INJURY_REPORT_URL,
  BETTING_URL,
  TRANSACTIONS_URL,
  LEAGUE_CONTRACTS_URL,
  DRAFT_URL,
  ALL_STAR_VOTING_URL,
  ALL_STAR_URL,
  PLAYOFFS_URL,
  QUESTIONS_URL,
  CONTRACTS_URL,
  SINGLE_VIDEO_URL,
  FREE_AGENCY_URL,
  FREE_AGENCY_TRACKER_URL,
  ACADEMY_EVENTS_URL,
  FANTASY_GROUPS_URL,
  ACADEMY_GIRLS_URL,
  ACADEMY_BOYS_URL,
  ACADEMY_VERIFIED_URL,
  PLAY_EVENTS_URL,
  PLAY_VERIFIED_URL,
  VIDEOS_BY_TAG_URL,
  PLAY_GIRLS_URL,
  PLAY_BOYS_URL,
  SINGLE_GAME_HIGHLIGHTS_URL,
  HELMET_HUNT_URL,
  CHAMPIONSHIP_SERIES_SCHEDULE,
  CHAMPIONSHIP_SERIES,
  PLAYER_MOVEMENT_URL,
  DRAFT_CENTRAL_URL,
} from './urls'
import {
  MatchupProvider,
  StandingsProvider,
  LeagueContractsProvider,
  AllStarProvider,
  FreeAgencyProvider,
  VideoGroupProvider,
} from '../Context'
import {
  AcademyContextLayout,
  LeagueContextLayout,
  PlayerProfileContextLayout,
  TeamContextLayout,
  QuestionContextLayout,
} from './ContextLayouts'
import { marketSlugs } from '../Config/Season/season'
import { HelmetHuntCommunityAch } from '../Components/CommunityAchievement/HelmetHuntCommunityAch'
import { Error, Loading as PageLoading } from '../Pages'
import { DraftContextLayout } from './ContextLayouts/DraftContextLayout'

const Homepage = lazy(() => import('../Pages/Home/Homepage'))
const Login = lazy(() => import('../Pages/Login/Login'))
const Schedule = lazy(() => import('../Pages/Schedule/Schedule'))
const Scores = lazy(() => import('../Pages/Scores/Scores'))
const Playoffs = lazy(() => import('../Pages/Playoffs/Playoffs'))
const Standings = lazy(() => import('../Pages/Standings/Standings'))
const MarketPage = lazy(() => import('../Pages/MarketPage/MarketPage'))
const Matchup = lazy(() => import('../Pages/Matchup/Matchup'))
const EventHub = lazy(() => import('../Pages/EventHub/EventHub'))
const Transactions = lazy(() => import('../Pages/Transactions/Transactions'))
const ChampionshipSeriesSchedule = lazy(
  () => import('../Pages/Schedule/ChampSeries/CSSchedule')
)

const Team = lazy(() => import('../Pages/Team/Team'))
const Roster = lazy(() => import('../Pages/Roster'))
const TeamSelect = lazy(() => import('../Pages/TeamSelect/TeamSelect'))

const AllStar = lazy(() => import('../Pages/AllStarGame/AllStar'))
const AllStarVote = lazy(() => import('../Pages/AllStarVote/AllStarVote'))

const Player = lazy(() => import('../Pages/Player/Player'))
const PlayerStats = lazy(() => import('../Pages/Player/PlayerStats'))
const PlayerMedia = lazy(() => import('../Pages/Player/PlayerMedia'))

const Draft = lazy(() => import('../Pages/Draft/Draft'))
const DraftBoardPage = lazy(() => import('../Pages/Draft/DraftBoardPage'))
const FreeAgency = lazy(() => import('../Pages/FreeAgency/FreeAgency'))
const InjuryReport = lazy(() => import('../Pages/InjuryReport/InjuryReport'))
const LeagueContracts = lazy(() => import('../Pages/LeagueContracts/LeagueContracts'))
const Predictions = lazy(() => import('../Pages/Predictions/Predictions'))

const GroupQuestions = lazy(
  () => import('../Pages/Questions/GroupQuestions/GroupQuestions')
)
const SingleQuestion = lazy(
  () => import('../Pages/Questions/SingleQuestion/SingleQuestion')
)

const SingleVideo = lazy(() => import('../Pages/Video/SingleVideo/SingleVideo'))
const VideoGroup = lazy(() => import('../Pages/Video/VideoGroup/VideoGroup'))

//PLL PLAY
const AcademyEvents = lazy(() => import('../Pages/Academy/Events/AcademyEvents'))
const AcademyBoys = lazy(() => import('../Pages/Academy/Boys/AcademyBoys'))
const AcademyGirls = lazy(() => import('../Pages/Academy/Girls/AcademyGirls'))
const AcademyVerified = lazy(() => import('../Pages/Academy/Verified/AcademyVerified'))

export const MainRoutes: React.FC<React.PropsWithChildren<unknown>> = () => (
  <Routes>
    <Route
      path="/login"
      element={
        <Suspense fallback={<PageLoading />}>
          <Login />
        </Suspense>
      }
    />
    <Route element={<QuestionContextLayout />}>
      <Route
        path={`${QUESTIONS_URL}/group/:groupId`}
        element={
          <Suspense fallback={<PageLoading />}>
            <GroupQuestions />
          </Suspense>
        }
      />
      <Route
        path={`${QUESTIONS_URL}/:questionId`}
        element={
          <Suspense fallback={<PageLoading />}>
            <SingleQuestion />
          </Suspense>
        }
      />
      <Route element={<LeagueContextLayout />}>
        <Route
          path="/"
          element={
            <Suspense fallback={<PageLoading />}>
              <Homepage />
            </Suspense>
          }
        />
        <Route
          path={`${SCHEDULE_URL}`}
          element={
            <Suspense fallback={<PageLoading />}>
              <Schedule />
            </Suspense>
          }
        />
        <Route
          path={`${SCHEDULE_URL}-et`}
          element={
            <Suspense fallback={<PageLoading />}>
              <Schedule />
            </Suspense>
          }
        />
        <Route
          path={`${TICKETS_URL}`}
          element={
            <Suspense fallback={<PageLoading />}>
              <Schedule />
            </Suspense>
          }
        />
        <Route
          path={`${SCORES_URL}`}
          element={
            <Suspense fallback={<PageLoading />}>
              <Scores />
            </Suspense>
          }
        />
        <Route
          path={`${CHAMPIONSHIP_SERIES_SCHEDULE}`}
          element={
            <Suspense fallback={<PageLoading />}>
              <ChampionshipSeriesSchedule />
            </Suspense>
          }
        />
        <Route
          path={`${CHAMPIONSHIP_SERIES}/tickets`}
          element={
            <Suspense fallback={<PageLoading />}>
              <div />
            </Suspense>
          }
        />
        <Route
          path={`${EVENT_HUB_URL}`}
          element={
            <Suspense fallback={<PageLoading />}>
              <EventHub />
            </Suspense>
          }
        />
        <Route
          path={`${TRANSACTIONS_URL}`}
          element={
            <Suspense fallback={<PageLoading />}>
              <Transactions />
            </Suspense>
          }
        />
        <Route
          path={`${ALL_STAR_URL}`}
          element={
            <AllStarProvider>
              <Suspense fallback={<PageLoading />}>
                <AllStar />
              </Suspense>
            </AllStarProvider>
          }
        />
        <Route
          path={`${ALL_STAR_VOTING_URL}`}
          element={
            <AllStarProvider>
              <Suspense fallback={<PageLoading />}>
                <AllStarVote />
              </Suspense>
            </AllStarProvider>
          }
        />
        <Route
          path={`${PLAYOFFS_URL}`}
          element={
            <Suspense fallback={<PageLoading />}>
              <Playoffs />
            </Suspense>
          }
        />

        <Route element={<TeamContextLayout />}>
          <Route
            path={`${TEAMS_URL}/:teamName`}
            element={
              <Suspense fallback={<PageLoading />}>
                <Team />
              </Suspense>
            }
          />
          <Route
            path={`${TEAMS_URL}/:teamName/roster`}
            element={
              <Suspense fallback={<PageLoading />}>
                <Roster />
              </Suspense>
            }
          />
        </Route>

        <Route element={<PlayerProfileContextLayout />}>
          <Route
            path={`${PLAYER_URL}/:slug`}
            element={
              <Suspense fallback={<PageLoading />}>
                <Player />
              </Suspense>
            }
          />
          <Route
            path={`${PLAYER_URL}/:slug/stats`}
            element={
              <Suspense fallback={<PageLoading />}>
                <PlayerStats />
              </Suspense>
            }
          />
          <Route
            path={`${PLAYER_URL}/:slug/media`}
            element={
              <Suspense fallback={<PageLoading />}>
                <PlayerMedia />
              </Suspense>
            }
          />
        </Route>
      </Route>
      <Route
        path={`${MATCHUP_URL}/:officialId`}
        element={
          <MatchupProvider>
            <Suspense fallback={<PageLoading />}>
              <Matchup />
            </Suspense>
          </MatchupProvider>
        }
      />
      <Route
        path={`${STANDINGS_URL}`}
        element={
          <StandingsProvider>
            <Suspense fallback={<PageLoading />}>
              <Standings />
            </Suspense>
          </StandingsProvider>
        }
      />
      <Route
        path={`${PREDICTIONS_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <Predictions />
          </Suspense>
        }
      />
      <Route
        path={`${INJURY_REPORT_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <InjuryReport />
          </Suspense>
        }
      />

      <Route
        path={`${LEAGUE_CONTRACTS_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <LeagueContractsProvider>
              <LeagueContracts />
            </LeagueContractsProvider>
          </Suspense>
        }
      />
      <Route
        path={`${CONTRACTS_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <LeagueContractsProvider>
              <LeagueContracts />
            </LeagueContractsProvider>
          </Suspense>
        }
      />
      <Route
        path={`${PLAYER_MOVEMENT_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <FreeAgencyProvider>
              <FreeAgency />
            </FreeAgencyProvider>
          </Suspense>
        }
      />
      <Route
        path={`${FREE_AGENCY_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <FreeAgencyProvider>
              <FreeAgency />
            </FreeAgencyProvider>
          </Suspense>
        }
      />
      <Route
        path={`${FREE_AGENCY_TRACKER_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <FreeAgencyProvider>
              <FreeAgency />
            </FreeAgencyProvider>
          </Suspense>
        }
      />

      <Route
        path={`${BETTING_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <Schedule />
          </Suspense>
        }
      />

      <Route element={<DraftContextLayout />}>
        <Route
          path={`${DRAFT_CENTRAL_URL}`}
          element={
            <Suspense fallback={<PageLoading />}>
              <Draft />
            </Suspense>
          }
        />
        <Route
          path={`${DRAFT_URL}`}
          element={
            <Suspense fallback={<PageLoading />}>
              <Draft />
            </Suspense>
          }
        />
        <Route
          path={`${DRAFT_URL}/board`}
          element={
            <Suspense fallback={<PageLoading />}>
              <DraftBoardPage />
            </Suspense>
          }
        />
      </Route>

      <Route
        path={`${VIDEOS_BY_TAG_URL}/:tag`}
        element={
          <VideoGroupProvider>
            <Suspense fallback={<PageLoading />}>
              <VideoGroup />
            </Suspense>
          </VideoGroupProvider>
        }
      />
      <Route
        path={`${SINGLE_GAME_HIGHLIGHTS_URL}/:eventSlug`}
        element={
          <VideoGroupProvider>
            <Suspense fallback={<PageLoading />}>
              <VideoGroup />
            </Suspense>
          </VideoGroupProvider>
        }
      />
      <Route
        path={`${SINGLE_VIDEO_URL}/:mediaId`}
        element={
          <Suspense fallback={<PageLoading />}>
            <SingleVideo />
          </Suspense>
        }
      />
    </Route>

    <Route element={<AcademyContextLayout />}>
      <Route element={<LeagueContextLayout />}>
        {marketSlugs.map((market) => (
          <Route
            key={market}
            path={`${market}`}
            element={
              <Suspense fallback={<PageLoading />}>
                <MarketPage />
              </Suspense>
            }
          />
        ))}
      </Route>

      <Route
        path={`${ACADEMY_EVENTS_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <AcademyEvents />
          </Suspense>
        }
      />
      <Route
        path={`${PLAY_EVENTS_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <AcademyEvents />
          </Suspense>
        }
      />
      <Route
        path={`${PLAY_BOYS_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <AcademyBoys />
          </Suspense>
        }
      />
      <Route
        path={`${PLAY_GIRLS_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <AcademyGirls />
          </Suspense>
        }
      />
      <Route
        path={`${ACADEMY_BOYS_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <AcademyBoys />
          </Suspense>
        }
      />
      <Route
        path={`${ACADEMY_GIRLS_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <AcademyGirls />
          </Suspense>
        }
      />
      <Route
        path={`${ACADEMY_VERIFIED_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <AcademyVerified />
          </Suspense>
        }
      />
      <Route
        path={`${PLAY_VERIFIED_URL}`}
        element={
          <Suspense fallback={<PageLoading />}>
            <AcademyVerified />
          </Suspense>
        }
      />
    </Route>

    <Route path={`${FANTASY_GROUPS_URL}`} element={<div />} />

    <Route
      path={`${TEAMS_URL}/:teamSlug/select`}
      element={
        <Suspense fallback={<PageLoading />}>
          <TeamSelect />
        </Suspense>
      }
    />

    <Route
      path={`${HELMET_HUNT_URL}`}
      element={
        <Suspense fallback={<PageLoading />}>
          <HelmetHuntCommunityAch />
        </Suspense>
      }
    />

    <Route path="*" element={<Error errorNo={404} />} />
  </Routes>
)
